/** The RFI register's reads and deletes, and the RFI PDF. RFIs are stored as
 *  contract items (see rfi_routes.tsx); the form still posts its own saves. */

import { getAuthHeaders } from '../utils/supabase/client';
import { SUPABASE_URL } from '../utils/supabase/url';
import type { RfiRecord, RfiDetail } from '../types/rfis';

export type { RfiRecord };

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b/rfis`;

async function request(path: string, init?: RequestInit): Promise<Response> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!response.ok) throw new Error(`RFI request failed (${response.status})`);
  return response;
}

export const RfiService = {
  /** One project's RFIs, or every RFI the caller can reach. */
  async list(projectId?: string): Promise<RfiRecord[]> {
    const response = await request(projectId ? `?projectId=${encodeURIComponent(projectId)}` : '');
    const data = await response.json();
    return Array.isArray(data?.rfis) ? data.rfis : [];
  },

  async detail(id: string): Promise<RfiDetail> {
    const response = await request(`/${encodeURIComponent(id)}`);
    return (await response.json()).rfi;
  },

  async remove(id: string): Promise<void> {
    await request(`/${id}`, { method: 'DELETE' });
  },

  /** Generates the RFI's PDF and hands it to the browser as a download. */
  async downloadPdf(id: string, rfiNumber: string): Promise<void> {
    const blob = await (await request(`/${id}/pdf`)).blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `RFI-${(rfiNumber || id.slice(0, 6)).replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
